import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { NetworkBrief } from "@/components/NetworkBrief";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Play, Pause, Clock } from "lucide-react";

function TimelineReplay({ network }) {
  const [events, setEvents] = useState([]);
  const [dates, setDates] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/intel/timeline", { params: { network } }).then(({ data }) => {
      setEvents(data);
      const uniq = Array.from(new Set(data.map((e) => e.date))).sort();
      setDates(uniq);
      setIdx(uniq.length ? uniq.length - 1 : 0);
    });
  }, [network]);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setInterval(() => {
      setIdx((i) => {
        if (i >= dates.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, 900);
    return () => clearInterval(timer.current);
  }, [playing, dates.length]);

  if (!events.length) return <div className="text-xs text-slate-400">No dated events.</div>;
  const cutoff = dates[idx];
  const visible = events.filter((e) => e.date <= cutoff);

  return (
    <div className="space-y-3" data-testid="timeline-replay">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" className="border-slate-300 gap-1.5" data-testid="timeline-play"
          onClick={() => { if (idx >= dates.length - 1) setIdx(0); setPlaying((p) => !p); }}>
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {playing ? "Pause" : "Replay"}
        </Button>
        <input type="range" min={0} max={dates.length - 1} value={idx} data-testid="timeline-slider"
          onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }} className="flex-1 accent-blue-600" />
        <span className="text-sm font-mono text-slate-600 w-24 text-right">{cutoff}</span>
      </div>
      <div className="text-xs text-slate-500 font-mono">{visible.length} of {events.length} events</div>
      <ol className="relative border-l-2 border-slate-200 pl-4 space-y-2 max-h-80 overflow-auto">
        {visible.map((e, i) => (
          <li key={e.id || `${e.date}-${e.kind}-${i}`} data-testid="timeline-event" className="relative">
            <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-white" />
            <div className="text-[11px] font-mono text-slate-400">{e.date} · {e.kind.replace(/_/g, " ")}</div>
            <div className="text-sm">{e.label}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function NetworkDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchNet = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/intel/network", { params: { name } }); setData(data); }
    catch { toast.error("Network not found"); navigate("/intel/networks"); }
    finally { setLoading(false); }
  }, [name, navigate]);

  useEffect(() => { fetchNet(); }, [fetchNet]);

  const analyze = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/intel/network/analyze", null, { params: { name } });
      setData((p) => ({ ...p, profile: res.profile, similar: res.similar, analyzed_at: res.analyzed_at }));
      toast.success("Network analysed");
    } catch (err) { toast.error(err.response?.data?.detail || "Analysis failed"); }
    finally { setBusy(false); }
  };

  if (loading || !data) return <div className="p-6 text-sm text-slate-500">Loading network…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="network-detail-page">
      <button onClick={() => navigate("/intel/networks")} data-testid="network-back-btn" className="text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-wider inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> All networks
      </button>

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-purple-700">Analytical intelligence</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">{name}</h1>
          <p className="text-sm text-slate-500 mt-1">{data.entity_count} entities · {data.analyzed_at ? `analysed ${data.analyzed_at.slice(0, 10)}` : "not analysed yet"}</p>
        </div>
        {!readOnly && (
          <Button data-testid="analyze-network-btn" onClick={analyze} disabled={busy} className="bg-purple-600 hover:bg-purple-700 gap-1.5">
            <Sparkles className="h-4 w-4" /> {busy ? "Analysing…" : data.profile ? "Re-analyse" : "Analyse network"}
          </Button>
        )}
      </header>

      <section className="border border-slate-200 bg-white rounded-md p-5">
        {data.profile ? (
          <NetworkBrief profile={data.profile} similar={data.similar || []} />
        ) : (
          <div className="text-center py-8 text-sm text-slate-500" data-testid="network-not-analyzed">
            This network has not been analysed yet.{readOnly ? "" : " Click “Analyse network” to generate an AI investigative brief."}
          </div>
        )}
      </section>

      <section className="border border-slate-200 bg-white rounded-md p-5">
        <h2 className="font-display font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><Clock className="h-4 w-4" /> Timeline replay</h2>
        <TimelineReplay network={name} />
      </section>
    </div>
  );
}
